import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';
import { CreateFormFeatureDialog, HelixLoaderComponent } from 'components';
import {
    isAttributeAssociationNonEmpty,
    updateEmptyParameterInputErrors,
} from '../../../components/paired-input-selection-widget/paired-input-selection-helper';
import { validationsMessages } from '../../../lib';
import { AttributeAssociationItem } from '../../edge-definitions/edge-definition-helper';
import {
    executeSmartContractConfig,
    SUCCESS_MESSAGE,
} from './create-or-update-smart-contract-helper';
import { ExecuteSmartContractForm } from 'pages/job-manager/components/create-execute-smart-contract-job/ExecuteSmartContractForm';
import {
    executeSmartContractJobDefaults,
    ExecuteSmartContractJobFormInputs,
    ExecuteSmartContractJobSchema,
    executeSmartContractJobStyles,
} from 'pages/job-manager/components/create-execute-smart-contract-job/create-execute-smart-contract-job-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useExecuteSmartContract, useSdo } from 'hooks';
import { useAuth } from '../../../core';

export const ExecuteSmartContract = (): React.JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const chain = queryParams.get('chain');
    const sdoId = queryParams.get('sdoId');
    const { getToken } = useAuth();
    const [selectedFunction, setSelectedFunction] = useState<string>('');
    const [deserializedCode, setDeserializedCode] = useState<any>();
    const [attributeAssociationList, setAttributeAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { mutate: executeSmartContract } =
        useExecuteSmartContract(SUCCESS_MESSAGE);

    const onSuccess = (data: any): void => {
        if (data.codeBase64) {
            setDeserializedCode(data.codeBase64);
        }
    };
    const onError = (): void => {};

    const { isLoading: smartContractDataLoading } = useSdo({
        chain: chain,
        sdoId: sdoId,
        onSuccess,
        onError,
    });

    const {
        control,
        formState: { errors },
        setValue,
        clearErrors,
        setError,
    } = useForm<ExecuteSmartContractJobFormInputs>({
        mode: 'all',
        resolver: yupResolver(ExecuteSmartContractJobSchema),
        defaultValues: executeSmartContractJobDefaults,
    });

    const handleExecuteSmartContractJob = (): void => {
        setIsLoading(true);
        executeSmartContract(
            {
                token: getToken(),
                chain: chain as string,
                sdoId: sdoId as string,
                func: selectedFunction,
                args: attributeAssociationList,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        navigate(
                            `${SMART_CONTRACT_PATHS.LANDING_PAGE}/${chain}/${sdoId}`
                        );
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleFunctionChange = (value: string): void => {
        setValue('func', value);
        setSelectedFunction(value);
        clearErrors('func');
    };

    const onAttributeAssociationsChanged = (
        list: AttributeAssociationItem[]
    ): void => {
        setAttributeAssociationList(list);
    };

    const handleBackButtonClick = (): void => {
        navigate(`${SMART_CONTRACT_PATHS.LANDING_PAGE}/${chain}/${sdoId}`);
    };

    const validateNonEmptyItems = isAttributeAssociationNonEmpty(
        attributeAssociationList
    );

    const handleExecuteSmartContract = (): void => {
        if (!chain) setError('chain', { type: 'required' });
        if (!deserializedCode) setError('smartContract', { type: 'required' });
        if (!selectedFunction) setError('func', { type: 'required' });
        if (!validateNonEmptyItems) {
            updateEmptyParameterInputErrors(
                attributeAssociationList,
                onAttributeAssociationsChanged,
                validationsMessages.required
            );
        }
        if (
            chain &&
            deserializedCode &&
            selectedFunction &&
            validateNonEmptyItems
        ) {
            handleExecuteSmartContractJob();
        }
    };

    if (smartContractDataLoading) {
        return <HelixLoaderComponent />;
    }

    return (
        <CreateFormFeatureDialog
            buttonText={'Execute Contract'}
            handleSubmit={(): void => {}}
            displaySubmitButton={false}
            customStyles={executeSmartContractJobStyles.root}
            isLoading={isLoading}
            handleCloseButton={handleBackButtonClick}
            handleBackButton={handleBackButtonClick}
            listConfig={executeSmartContractConfig}
        >
            <ExecuteSmartContractForm
                control={control}
                errors={errors}
                encodedQuery={deserializedCode}
                handleSubmit={handleExecuteSmartContract}
                onFunctionChange={handleFunctionChange}
                attributeAssociationList={attributeAssociationList}
                handleAttributeAssociationsChanged={
                    onAttributeAssociationsChanged
                }
            />
        </CreateFormFeatureDialog>
    );
};
